import { getTenantFromToken } from "config/helpers/common_helper";
import { extractEhrInfoFromFhirBasic } from "config/helpers/extractEhrInfo_helper";
import { toast } from "react-toastify";
import { store } from "redux/store";
import { HTTPBridgeService, HTTPLocalService } from "./http";
import dayjs from "dayjs";

const formatPemToMultiline = (pem) => {
  if (!pem) return "";
  return pem
    .replace(/\\n/g, "\n") // Convert escaped newlines to actual newlines
    .replace(/(-----BEGIN [\w\s]+-----)\s*/g, "$1\n")
    .replace(/\s*(-----END [\w\s]+-----)/g, "\n$1")
    .trim();
};

const getConnectionId = () => {
  const data = store?.getState();
  const connectionId = data?.connectedEHR?.connectionId;
  if (!connectionId) {
    toast.error("No EHR Connection has been established");
    throw new Error("ConnectionId Not Found");
  }
  return connectionId;
};

export const CREATE_CONNECTION_TO_BRIDGE = async (data, setIsSyncActive) => {
  const formattedSecret = data.clientSecret
    ? formatPemToMultiline(data.clientSecret)
    : "";

  const payload = {
    ...data,
    clientSecret: formattedSecret,
    prodEnvironment: true,
  };

  try {
    const url = `/api/tenants/${getTenantFromToken()}/connections`;
    const response = await HTTPBridgeService.post(url, payload);

    const extractedData = extractEhrInfoFromFhirBasic(response?.data);
    const { connectionId, ehrName } = extractedData;

    toast.success("EHR Connection Successful");
    return { connectionId, ehrName };
  } catch (error) {
    toast.error(
      error?.response?.data?.message || "Failed to create connection."
    );
    console.error(
      "Error establishing connection to the bridge:",
      error?.response?.data || error.message
    );
  }
};

/*export const CREATE_CONNECTION_TO_BRIDGE = async (data, setIsSyncActive) => {
  //Accepts data with the following fields: ehrName, connectionName, accountId, clientId, clientSecret
  //Sends data to the bridge to create a Connection ID, which is to be used with every subsequent call as x-connection-id
  console.log("payload for create connection:", JSON.stringify(data, null, 2));
  try {
    const url = `/api/tenants/${getTenantFromToken()}/connections`;
    const response = await HTTPBridgeService.post(url, {
      ...data,
      //! TODO Need to change to true once pushed to prod
      prodEnvironment: false,
    });
    const extractedData = extractEhrInfoFromFhirBasic(response?.data);
    const { connectionId, ehrName } = extractedData;
    if(connectionId?.trim() && ehrName?.trim()){
      toast.success("EHR Connection Successful");
    }
    return { connectionId, ehrName };
  } catch (error) {
    toast.error(error?.response?.data?.message);
    console.log(
      "An Error Occurred while trying to establish a connection to the bridge",
      error?.response?.data?.message
    );
  }
};*/

export const CREATE_ECW_BULK_CONNECTION = async (data) => {
  try {
    const url = `/api/tenants/${getTenantFromToken()}/connections/bulk/ecw`;

    const response = await HTTPBridgeService.post(url, {
      ...data,
      //! TODO Need to change to true once pushed to prod
      prodEnvironment: true,
    });

    return response?.data;
  } catch (error) {
    toast.error(error?.response?.data?.message);
    console.log(
      "An Error Occurred while trying to establish a connection to the bridge",
      error?.response?.data?.message
    );
  }
};

export const CREATE_CONNECTION_TO_BRIDGE_WITH_SSO = async (data) => {
  //Accepts data with the following fields: ehrName, connectionName, accountId, clientId, clientSecret
  //Response will have a url where user is redirectd to authenticate themselves with SSO
  try {
    const url = `/api/tenants/${getTenantFromToken()}/connections/sso`;
    const response = await HTTPBridgeService.post(url, {
      ...data,
      //! TODO Need to change to true once pushed to prod
      prodEnvironment: true,
    });
    const SSOAuthUrl = response?.data?.url;
    window.open(SSOAuthUrl, "_self");
  } catch (error) {
    toast.error(error?.response?.data?.message);
    console.log(
      "An Error Occurred while trying to establish a connection to the bridge",
      error?.response?.data?.message
    );
  }
};

export const STORE_CONNECTION_DETAILS_IN_BASIC = async (
  connectionDetails,
  isSync = false
) => {
  const response1 = await HTTPBridgeService.post(
    `/api/tenants/${getTenantFromToken()}/connections/create`,
    connectionDetails
  );
  const extractedData = extractEhrInfoFromFhirBasic(response1?.data);
  const { connectionId, ehrName } = extractedData;
  //Sync EHR Patients into Noki with the given connectionId
  if (!isSync) {
    await SYNC_EHR_PATIENT(connectionId, ehrName);
  }
  toast.success("EHR Connection Successful");
  return { connectionId, ehrName };
};

export const CREATE_EHR_PATIENT = async (payload) => {
  const connectionId = getConnectionId();
  const response = await HTTPBridgeService.post(
    `/api/tenants/${getTenantFromToken()}/create/patient/${connectionId}`,
    payload,
    {
      headers: {
        "x-connection-id": connectionId,
      },
    }
  );
  return response;
};

export const GET_EHR_PATIENT_BY_ID = async (id) => {
  //Accepts Patient ID fetches the given patient
  if (id === undefined) return;
  try {
    const connectionId = getConnectionId();

    const response = await HTTPBridgeService.get(`/api/patient/${id}`, {
      headers: {
        "x-connection-id": connectionId,
      },
    });

    return response?.data;
  } catch (error) {
    console.log("An Error occurred while fetching patients", error);
  }
};

export const GET_EXTERNAL_PRACTITIONER_ID = async (id) => {
  try {
    const connectionId = getConnectionId();
    const response = await HTTPBridgeService.get(
      `/api/tenants/${getTenantFromToken()}/encounter/${id}`,
      {
        headers: {
          "x-connection-id": connectionId,
        },
      }
    );
    return response?.data;
  } catch (error) {
    console.log(
      "An Error occurred while fetching encounter information by encounterId"
    );
  }
};

export const SYNC_EHR_PATIENT = async (connectionId, ehrName, retryId = 0) => {
  //Syncs patient information from Bridge to Noki Fhir Store
  try {
    //const queryParam = ehrName === "NextGen" ? "?top=0" : "";
    let queryParam = "";

    if (ehrName === "NextGen") {
      queryParam = "?top=0";
    } else if (ehrName === "Cerner") {
      const givenValue = "joe";
      queryParam = `?given=${givenValue}`;
    }

    const url = `/api/tenants/${getTenantFromToken()}/patient/sync/${connectionId}${queryParam}`;

    const response = await HTTPBridgeService.get(url, {
      headers: {
        "x-connection-id": connectionId,
      },
    });
    //Checking if the API has created the resources correctly, if not calling GET_EHR_PATIENT_BY_ID recurrsively on 500 ms intervals
    if (response?.data?.success) {
      return;
    } else if (
      response?.data?.message?.includes("Patient sync operation failed")
    ) {
      toast.error("An Error occurred while fetching patients from the EHR");
      return;
    } else if (response?.data?.fhirtask?.status === "failed" && retryId < 5) {
      //Need to add a fallback limit for recurrsively calling GET_PATIENT function in case api fails
      await new Promise((resolve) => setTimeout(resolve, 30000));
      await SYNC_EHR_PATIENT(connectionId, retryId + 1);
    } else {
      // toast.error(
      //   "An Error occurred while fetching patients from the EHR Maximum retries hit"
      // );
      return;
    }
  } catch (error) {
    console.log("An Error occurred while fetching patients from the EHR");
  }
};

export const GET_EHR_CONNECTION_STATUS = async () => {
  try {
    const url = `/api/tenants/${getTenantFromToken()}/connections`;
    const response = await HTTPBridgeService.get(url);
    const data = response?.data?.entry;
    return extractEhrInfoFromFhirBasic(
      Array.isArray(data) ? data?.[0]?.resource : data
    );
  } catch (error) {
    console.log("Error Occurred while Fetching EHR status", error);
  }
};

export const SYNC_EHR_ENCOUNTERS_WITH_PATIENTID = async (patientId) => {
  const connectionId = getConnectionId();
  try {
    if (!connectionId) throw new Error("ConnectionId is Invalid");
    if (!patientId) throw new Error("PatientId is Invalid");
    const url = `/api/tenants/${getTenantFromToken()}/create/encounter/${connectionId}`;
    const {
      data: { entry: data },
    } = await HTTPBridgeService.post(url, { patientId });
    return data;
  } catch (error) {
    console.log("Error Occurred while Syncing Ecounters", error);
  }
};

export const SYNC_EHR_ENCOUNTER_RESOURCES_WITH_PATIENTID = async (
  patientId,
  externalPatientId
) => {
  const connectionId = getConnectionId();
  try {
    if (!connectionId) throw new Error("ConnectionId is Invalid");
    if (!patientId) throw new Error("PatientId is Invalid");
    const url = `/api/tenants/${getTenantFromToken()}/patient/create/clinicalnote/${connectionId}`;
    const {
      data: { entry: data },
    } = await HTTPBridgeService.post(url, { patientId, externalPatientId });
    return data;
  } catch (error) {
    console.log("Error Occurred while Syncing Ecounters", error);
  }
};

export const SYNC_EHR_ENCOUNTER_RESOURCES_WITH_PATIENTID_FOR_ENLIV = async (
  patientId
) => {
  debugger;
  const connectionId = getConnectionId();
  try {
    if (!connectionId) throw new Error("ConnectionId is Invalid");
    if (!patientId) throw new Error("PatientId is Invalid");
    const url = `/api/tenants/${getTenantFromToken()}/patient/create/clinicalnotes/${connectionId}`;
    const { data: encountersArray } = await HTTPLocalService.post(url, {
      patientId,
    });

    // Transform to FHIR Bundle format
    const entry = Array.isArray(encountersArray)
      ? encountersArray.map((enc) => ({ resource: enc }))
      : [];

    return { entry };
  } catch (error) {
    console.log("Error Occurred while Syncing Ecounters", error);
  }
};

export const PUSH_DOCUMENT_REFERENCE_WITH_ENCOUNTERID = async (encounterId) => {
  const connectionId = getConnectionId();

  if (!connectionId) throw new Error("ConnectionId is Invalid");
  if (!encounterId) throw new Error("EncounterId is Invalid");

  const url = `/api/tenants/${getTenantFromToken()}/create/documentreference/${connectionId}`;
  const data = await HTTPBridgeService.post(url, { encounterId });
  return data;
};

export const PUSH_SOAP_NOTE = async (soapNote) => {
  debugger;
  const connectionId = getConnectionId();

  if (!connectionId) throw new Error("ConnectionId is Invalid");
  if (!soapNote) throw new Error("Soap Note is Invalid");

  const url = `/api/tenants/${getTenantFromToken()}/create/soap/${connectionId}`;
  const data = await HTTPBridgeService.post(url, { ...soapNote });
  console.log(JSON.stringify(data,null,2));
  return data;
};

export const SEND_SOAP_NOTE_TO_ENLIV = (soapNote) => {
  if (!soapNote) throw new Error("Soap Note is invalid");

  // Send a postMessage to Enliv (parent app)
  window.parent.postMessage(
    {
      type: "soap-note-data",
      payload: soapNote,
    },
    "*"
  );
};

export const SEARCH_PRACTITIONER_DATA = async ({
  firstName,
  lastName,
  dateOfBirth,
}) => {
  const connectionId = getConnectionId();
  const tenantId = getTenantFromToken();

  const params = new URLSearchParams();
  if (lastName) params.append("family", lastName);
  if (firstName) params.append("given", firstName);
  if (dateOfBirth) {
    const formattedDOB = dayjs(dateOfBirth).isValid()
      ? dayjs(dateOfBirth).format("YYYY-MM-DD")
      : null;

    if (formattedDOB) {
      params.append("birthdate", formattedDOB);
    } else {
      console.warn(
        "Invalid dateOfBirth passed to SEARCH_PRACTITIONER_DATA:",
        dateOfBirth
      );
    }
  }

  const url = `/api/tenants/${tenantId}/practitioner?${params.toString()}`;
  console.log("url:", url);
  const practitionerData = await HTTPBridgeService.get(url, {
    headers: {
      "x-connection-id": connectionId,
    },
  });

  return practitionerData?.data?.entry;
};
